import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  CreditCard,
  Wallet,
  Building2,
  Banknote,
  Save,
  Loader2,
  Settings,
  CheckCircle2,
  XCircle,
  ArrowUpDown,
  Eye,
  EyeOff,
  TestTube,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProcessorIcon = ({ code, className }) => {
  switch (code) {
    case 'stripe':
      return <CreditCard className={className} />;
    case 'paypal':
      return <Wallet className={className} />;
    case 'bank_transfer':
      return <Building2 className={className} />;
    case 'cash':
      return <Banknote className={className} />;
    default:
      return <CreditCard className={className} />;
  }
};

const PaymentProcessors = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processors, setProcessors] = useState([]);
  const [selectedProcessor, setSelectedProcessor] = useState(null);
  const [editData, setEditData] = useState({});
  const [showSecrets, setShowSecrets] = useState(false);
  
  const canEdit = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    fetchProcessors();
  }, []);

  const fetchProcessors = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/payment-processors`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch payment processors');
      }
      
      const data = await response.json();
      setProcessors(data.processors || []);
    } catch (error) {
      console.error('Error fetching processors:', error);
      toast.error('Gabim gjatë ngarkimit të procesorëve të pagesave');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (processor, newValue) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/payment-processors/${processor.id}/toggle?is_active=${newValue}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to toggle processor');
      }
      
      toast.success(`${processor.display_name} u ${newValue ? 'aktivizua' : 'çaktivizua'}`);
      await fetchProcessors();
    } catch (error) {
      console.error('Error toggling processor:', error);
      toast.error('Gabim gjatë ndryshimit të statusit');
    }
  };

  const openEditDialog = (processor) => {
    const preview = processor.config_secret_preview || {};
    setSelectedProcessor(processor);
    setEditData({
      display_name: processor.display_name,
      description: processor.description,
      environment: processor.environment,
      fee_percent: processor.fee_percent,
      fee_fixed: processor.fee_fixed,
      min_amount: processor.min_amount,
      max_amount: processor.max_amount,
      sort_order: processor.sort_order,
      available_for_client: processor.available_for_client,
      config_public: processor.config_public || {},
      config_secret: {
        ...preview,
        key_secret: ""
      }
    });
    setShowSecrets(false);
  };

  const saveProcessor = async () => {
    if (!selectedProcessor) return;
    
    try {
      setSaving(true);
      
      const updatePayload = { ...editData };
      const secretKeys = Object.keys(editData.config_secret || {});
      
      if (secretKeys.length === 0) {
        // No secrets touched at all - skip secret update
        delete updatePayload.config_secret;
      }
      
      const response = await fetch(`${API_URL}/api/admin/payment-processors/${selectedProcessor.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatePayload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save processor');
      }
      
      toast.success('Procesori u ruajt me sukses');
      setSelectedProcessor(null);
      await fetchProcessors();
    } catch (error) {
      console.error('Error saving processor:', error);
      toast.error(error.message || 'Gabim gjatë ruajtjes');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (processor) => {
    try {
      toast.loading('Duke testuar lidhjen...', { id: 'test-connection' });
      
      const response = await fetch(`${API_URL}/api/admin/payment-processors/${processor.id}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(result.message || 'Lidhja u testua me sukses', { id: 'test-connection' });
      } else {
        toast.error(result.message || 'Testi dështoi', { id: 'test-connection' });
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error('Gabim gjatë testimit', { id: 'test-connection' });
    }
  };

  const renderSecretFields = (code) => {
    switch (code) {
      case 'stripe':
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Publishable Key</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                placeholder="pk_test_..."
                value={editData.config_secret?.publishable_key || ''}
                onChange={(e) => setEditData({
                  ...editData,
                  config_secret: { ...editData.config_secret, publishable_key: e.target.value }
                })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Secret Key</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                placeholder="sk_test_..."
                value={editData.config_secret?.secret_key || ''}
                onChange={(e) => setEditData({
                  ...editData,
                  config_secret: { ...editData.config_secret, secret_key: e.target.value }
                })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Webhook Secret</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                placeholder="whsec_..."
                value={editData.config_secret?.webhook_secret || ''}
                onChange={(e) => setEditData({
                  ...editData,
                  config_secret: { ...editData.config_secret, webhook_secret: e.target.value }
                })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
          </div>
        );
      case 'paypal':
        return (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-blue-400 text-sm">
                <strong>Hapat:</strong> 1) Shkoni në <a href="https://developer.paypal.com/dashboard/applications/sandbox" target="_blank" rel="noopener noreferrer" className="underline">PayPal Developer Dashboard</a> 
                → 2) Krijoni ose zgjidhni një App → 3) Kopjoni Client ID dhe Secret → 4) Krijoni Webhook me URL: <code className="bg-zinc-800 px-1 rounded">{API_URL}/api/webhook/paypal</code>
              </p>
            </div>
            <div>
              <Label className="text-zinc-300">Client ID</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                placeholder="AV..."
                value={editData.config_secret?.client_id || ''}
                onChange={(e) => setEditData({
                  ...editData,
                  config_secret: { ...editData.config_secret, client_id: e.target.value }
                })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Client Secret</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                placeholder="EM..."
                value={editData.config_secret?.client_secret || ''}
                onChange={(e) => setEditData({
                  ...editData,
                  config_secret: { ...editData.config_secret, client_secret: e.target.value }
                })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Webhook ID</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                placeholder="WH-..."
                value={editData.config_secret?.webhook_id || ''}
                onChange={(e) => setEditData({
                  ...editData,
                  config_secret: { ...editData.config_secret, webhook_id: e.target.value }
                })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
              <p className="text-zinc-500 text-xs mt-1">Webhook ID merret pas krijimit të Webhook në PayPal Dashboard</p>
            </div>
          </div>
        );
      case 'pok':
        return (
          <div className="space-y-4">
            {selectedProcessor?.has_secrets && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-emerald-300 text-sm">
                  Kredencialet ekzistuese janë ruajtur në DB. Për siguri, Key Secret nuk rikthehet i lexueshëm.
                  Lëre bosh nëse nuk do ta ndryshosh.
                </p>
              </div>
            )}
            <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
              <p className="text-orange-400 text-sm">
                <strong>Hapat:</strong> 1) Shkoni në <a href="https://payments.doc.pokpay.io/" target="_blank" rel="noopener noreferrer" className="underline">POK Payments Dashboard</a> 
                → 2) Merrni Key ID, Key Secret dhe Merchant ID → 3) Vendosni Webhook URL: <code className="bg-zinc-800 px-1 rounded">{API_URL}/api/pok/webhook</code>
              </p>
            </div>
            <div>
              <Label className="text-zinc-300">Mjedisi</Label>
              <Select
                value={editData.config_secret?.api_base_url === 'https://api.pokpay.io' ? 'production' : 'staging'}
                onValueChange={(val) => setEditData({
                  ...editData,
                  config_secret: { 
                    ...editData.config_secret, 
                    api_base_url: val === 'production' ? 'https://api.pokpay.io' : 'https://api-staging.pokpay.io' 
                  }
                })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="staging">🧪 Staging (Test)</SelectItem>
                  <SelectItem value="production">🚀 Production (Live)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-zinc-500 text-xs mt-1">
                Staging: api-staging.pokpay.io | Production: api.pokpay.io
              </p>
            </div>
            <div>
              <Label className="text-zinc-300">Merchant ID</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                placeholder={selectedProcessor?.has_secrets ? 'Ruajtur (ndrysho vetëm nëse duhet)' : 'Merchant ID...'}
                value={editData.config_secret?.merchant_id || ''}
                onChange={(e) => setEditData({
                  ...editData,
                  config_secret: { ...editData.config_secret, merchant_id: e.target.value }
                })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Key ID</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                placeholder={selectedProcessor?.has_secrets ? 'Ruajtur (ndrysho vetëm nëse duhet)' : 'Key ID...'}
                value={editData.config_secret?.key_id || ''}
                onChange={(e) => setEditData({
                  ...editData,
                  config_secret: { ...editData.config_secret, key_id: e.target.value }
                })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Key Secret</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                placeholder={selectedProcessor?.has_secrets ? 'Ruajtur (plotëso vetëm për ndryshim)' : 'Key Secret...'}
                value={editData.config_secret?.key_secret || ''}
                onChange={(e) => setEditData({
                  ...editData,
                  config_secret: { ...editData.config_secret, key_secret: e.target.value }
                })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
          </div>
        );
      case 'bank_transfer':
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">IBAN</Label>
              <Input
                type="text"
                placeholder="AL00 0000 0000 0000"
                value={editData.config_public?.iban || ''}
                onChange={(e) => setEditData({
                  ...editData,
                  config_public: { ...editData.config_public, iban: e.target.value }
                })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Emri i Bankës</Label>
              <Input
                type="text"
                value={editData.config_public?.bank_name || ''}
                onChange={(e) => setEditData({
                  ...editData,
                  config_public: { ...editData.config_public, bank_name: e.target.value }
                })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Përfituesi</Label>
              <Input
                type="text"
                value={editData.config_public?.beneficiary || ''}
                onChange={(e) => setEditData({
                  ...editData,
                  config_public: { ...editData.config_public, beneficiary: e.target.value }
                })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
        );
      default:
        return (
          <p className="text-zinc-500 text-sm">Ky procesor nuk kërkon konfigurim sekret.</p>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="payment-processors-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-processors-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-outfit">Procesorët e Pagesave</h1>
          <p className="text-zinc-400 mt-1">Menaxho metodat e pagesës për aplikacionin mobil</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchProcessors}
          className="border-zinc-700 hover:border-zinc-600"
          data-testid="refresh-processors-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Rifresko
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-primary/10 to-transparent border-primary/20">
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-white font-medium">Sinkronizimi në Kohë Reale</p>
              <p className="text-zinc-400 text-sm mt-1">
                Ndryshimet që bëni këtu reflektohen automatikisht në aplikacionin mobil. 
                Klientët do të shohin vetëm metodat e pagesës që janë aktive.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Warning for non-admins */}
      {!canEdit && (
        <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <p className="text-amber-400 text-sm">
            Vetëm Super Admin mund të ndryshojë procesorët e pagesave.
          </p>
        </div>
      )}

      {/* Processors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {processors.map((processor) => (
          <Card 
            key={processor.id} 
            className={`bg-zinc-900/50 border-white/10 transition-all hover:border-white/20 ${
              processor.is_active ? 'ring-1 ring-green-500/20' : ''
            }`}
            data-testid={`processor-card-${processor.code}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${processor.config_public?.color || '#6366f1'}20` }}
                  >
                    <ProcessorIcon 
                      code={processor.code} 
                      className="w-6 h-6" 
                      style={{ color: processor.config_public?.color || '#6366f1' }}
                    />
                  </div>
                  <div>
                    <CardTitle className="text-white text-lg">{processor.display_name}</CardTitle>
                    <CardDescription className="text-sm">
                      {processor.description}
                    </CardDescription>
                  </div>
                </div>
                <Switch
                  checked={processor.is_active}
                  onCheckedChange={(checked) => handleToggle(processor, checked)}
                  disabled={!canEdit}
                  data-testid={`toggle-${processor.code}`}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status Badges */}
              <div className="flex flex-wrap gap-2">
                <Badge variant={processor.is_active ? 'default' : 'secondary'} className={processor.is_active ? 'bg-green-500/20 text-green-400' : ''}>
                  {processor.is_active ? (
                    <><CheckCircle2 className="w-3 h-3 mr-1" /> Aktiv</>
                  ) : (
                    <><XCircle className="w-3 h-3 mr-1" /> Joaktiv</>
                  )}
                </Badge>
                <Badge variant="outline" className="border-zinc-700">
                  {processor.environment === 'live' ? 'Live' : 'Test'}
                </Badge>
                {processor.available_for_client ? (
                  <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                    Për Klientët
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-amber-500/50 text-amber-400">
                    Vetëm Admin
                  </Badge>
                )}
              </div>

              {/* Fees & Limits */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 rounded-lg bg-zinc-800/50">
                  <p className="text-zinc-500">Tarifa</p>
                  <p className="text-white font-medium">
                    {processor.fee_percent}% + {processor.fee_fixed / 100} LEK
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-zinc-800/50">
                  <p className="text-zinc-500">Limitet</p>
                  <p className="text-white font-medium">
                    {processor.min_amount} - {processor.max_amount} LEK
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-zinc-700 hover:border-zinc-600"
                  onClick={() => openEditDialog(processor)}
                  disabled={!canEdit}
                  data-testid={`edit-${processor.code}`}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Konfiguro
                </Button>
                {processor.code !== 'cash' && processor.code !== 'bank_transfer' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-zinc-700 hover:border-zinc-600"
                    onClick={() => testConnection(processor)}
                    data-testid={`test-${processor.code}`}
                  >
                    <TestTube className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!selectedProcessor} onOpenChange={(open) => !open && setSelectedProcessor(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-3">
              {selectedProcessor && (
                <ProcessorIcon code={selectedProcessor.code} className="w-6 h-6" />
              )}
              Konfiguro {selectedProcessor?.display_name}
            </DialogTitle>
            <DialogDescription>
              Ndrysho parametrat e këtij procesori të pagesave
            </DialogDescription>
          </DialogHeader>

          {selectedProcessor && (
            <div className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-white font-medium">Informacione Bazë</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Emri i Shfaqur</Label>
                    <Input
                      value={editData.display_name || ''}
                      onChange={(e) => setEditData({ ...editData, display_name: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Mjedisi</Label>
                    <Select
                      value={editData.environment}
                      onValueChange={(val) => setEditData({ ...editData, environment: val })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="test">Test</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300">Përshkrimi</Label>
                  <Input
                    value={editData.description || ''}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>

              {/* Fees & Limits */}
              <div className="space-y-4">
                <h3 className="text-white font-medium">Tarifat & Limitet</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Tarifa (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editData.fee_percent || 0}
                      onChange={(e) => setEditData({ ...editData, fee_percent: parseFloat(e.target.value) || 0 })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Tarifa Fikse (qindarka)</Label>
                    <Input
                      type="number"
                      value={editData.fee_fixed || 0}
                      onChange={(e) => setEditData({ ...editData, fee_fixed: parseInt(e.target.value) || 0 })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Shuma Minimale (LEK)</Label>
                    <Input
                      type="number"
                      value={editData.min_amount || 100}
                      onChange={(e) => setEditData({ ...editData, min_amount: parseInt(e.target.value) || 100 })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Shuma Maksimale (LEK)</Label>
                    <Input
                      type="number"
                      value={editData.max_amount || 50000}
                      onChange={(e) => setEditData({ ...editData, max_amount: parseInt(e.target.value) || 50000 })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>
              </div>

              {/* Display Settings */}
              <div className="space-y-4">
                <h3 className="text-white font-medium">Cilësimet e Shfaqjes</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Renditja</Label>
                    <Input
                      type="number"
                      value={editData.sort_order || 1}
                      onChange={(e) => setEditData({ ...editData, sort_order: parseInt(e.target.value) || 1 })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <Switch
                      checked={editData.available_for_client}
                      onCheckedChange={(checked) => setEditData({ ...editData, available_for_client: checked })}
                    />
                    <Label className="text-zinc-300">I disponueshëm për klientët</Label>
                  </div>
                </div>
              </div>

              {/* Secret Configuration */}
              {selectedProcessor.code !== 'cash' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white font-medium">Kredencialet</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowSecrets(!showSecrets)}
                      className="text-zinc-400 hover:text-white"
                    >
                      {showSecrets ? (
                        <><EyeOff className="w-4 h-4 mr-2" /> Fshih</>
                      ) : (
                        <><Eye className="w-4 h-4 mr-2" /> Shfaq</>
                      )}
                    </Button>
                  </div>
                  <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                    {renderSecretFields(selectedProcessor.code)}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-zinc-800">
                <Button
                  variant="outline"
                  onClick={() => setSelectedProcessor(null)}
                  className="flex-1 border-zinc-700"
                >
                  Anulo
                </Button>
                <Button
                  onClick={saveProcessor}
                  disabled={saving}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Ruaj
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentProcessors;
